import { useState, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Search, Sparkles, Filter, X, ArrowRight, Eye, Info, Droplet, Sun, ShieldAlert } from "lucide-react";
import { getCorrectPlantImage } from "../utils";
import { SmartImage } from "./SmartImage";
import { BotanicalIcon } from "./BotanicalIcon";

interface PlantItem {
  id: string;
  name: string;
  scientific: string;
  category: string; // Internal filter category
  badge: string; // Display category badge
  image: string;
  light: string;
  water: string;
  difficulty: "Easy" | "Medium" | "Expert";
  description: string;
  toxicity: string;
}

interface PlantDatabaseProps {
  onPlantClick?: (slug: string) => void;
}

export default function PlantDatabase({ onPlantClick }: PlantDatabaseProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedPlant, setSelectedPlant] = useState<PlantItem | null>(null);

  // Filter Categories requested: Indoor Plants, Outdoor Plants, Flowers, Succulents, Trees, Herbs, Fruits, Vegetables
  const categories = [
    "All",
    "Indoor Plants",
    "Outdoor Plants",
    "Flowers",
    "Succulents",
    "Trees",
    "Herbs",
    "Fruits & Vegetables"
  ];

  const plantList: PlantItem[] = [
    {
      id: "plant-1",
      name: "Monstera Deliciosa",
      scientific: "Monstera deliciosa",
      category: "Indoor Plants",
      badge: "Indoor Plant",
      image: "https://images.unsplash.com/photo-1614594975525-e45190c55d0b?q=80&w=600&auto=format&fit=crop",
      light: "Bright indirect sunlight",
      water: "Every 1-2 weeks",
      difficulty: "Easy",
      description: "Famous for its natural leaf holes, the Swiss Cheese plant is a striking block of tropical greenery that makes a bold statement in any workspace.",
      toxicity: "Toxic to cats and dogs if ingested"
    },
    {
      id: "plant-2",
      name: "Snake Plant",
      scientific: "Sansevieria trifasciata",
      category: "Indoor Plants",
      badge: "Air Purifying",
      image: "https://images.unsplash.com/photo-1596547609652-9cf5d8d76921?q=80&w=600&auto=format&fit=crop",
      light: "Low to partial bright light",
      water: "Every 2-3 weeks (allow to dry completely)",
      difficulty: "Easy",
      description: "An incredibly hardy air-purifying plant that converts CO2 into oxygen overnight. Excellent for bedrooms and offices with minimal windows.",
      toxicity: "Mildly toxic to pets"
    },
    {
      id: "plant-3",
      name: "Peace Lily",
      scientific: "Spathiphyllum",
      category: "Flowers",
      badge: "Flowering Plant",
      image: "https://images.unsplash.com/photo-1622383563227-04401ab4e5ea?q=80&w=600&auto=format&fit=crop",
      light: "Medium indirect shade",
      water: "Keep soil consistently damp, mist occasionally",
      difficulty: "Medium",
      description: "Elegant green foliage crowned with pristine white blooms. Excellent at filtering interior VOC gases and raising domestic ambient humidity.",
      toxicity: "Contains calcium oxalate crystals, toxic to pets"
    },
    {
      id: "plant-4",
      name: "Aloe Vera",
      scientific: "Aloe barbadensis miller",
      category: "Succulents",
      badge: "Medicinal Plant",
      image: "https://images.unsplash.com/photo-1599599810769-bcde5a160d32?q=80&w=600&auto=format&fit=crop",
      light: "Bright direct sunlight",
      water: "Deeply but infrequently (every 3 weeks)",
      difficulty: "Easy",
      description: "Thick fleshy stems packed with soothing, nutrient-rich medicinal gel used to soothe burns and optimize cosmetic skin care configurations.",
      toxicity: "Mildly toxic if eaten by pets"
    },
    {
      id: "plant-5",
      name: "English Lavender",
      scientific: "Lavandula angustifolia",
      category: "Herbs",
      badge: "Aromatic Herb",
      image: "https://images.unsplash.com/photo-1471943311424-646960669fbc?q=80&w=600&auto=format&fit=crop",
      light: "Full direct sun",
      water: "Once a week, highly drought tolerant",
      difficulty: "Medium",
      description: "A highly aromatic herb with beautiful purple flower spikes, frequently used for essential oils, sleep wellness, and kitchen craft recipes.",
      toxicity: "Safe for humans, keep away from curious pets"
    },
    {
      id: "plant-6",
      name: "Japanese Maple",
      scientific: "Acer palmatum",
      category: "Trees",
      badge: "Ornamental Tree",
      image: "https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?q=80&w=600&auto=format&fit=crop",
      light: "Full sun to partial shade",
      water: "Water deeply 1-2 times weekly",
      difficulty: "Expert",
      description: "A legendary deciduous garden masterpiece known for its premium lace-like foliage that glows with striking shades of burgundy and copper red.",
      toxicity: "Non-toxic"
    },
    {
      id: "plant-7",
      name: "Sweet Basil",
      scientific: "Ocimum basilicum",
      category: "Fruits & Vegetables",
      badge: "Culinary Garden",
      image: "https://images.unsplash.com/photo-1618375531910-a3cfd39317b9?q=80&w=600&auto=format&fit=crop",
      light: "Direct sunlight, 6+ hours daily",
      water: "Daily or every other day, keep well-drained",
      difficulty: "Easy",
      description: "The gold standard of aromatic herbs. Fresh sweet basil leaves are essential for pestos, culinary finishes, and culinary balcony gardens.",
      toxicity: "Completely safe"
    },
    {
      id: "plant-8",
      name: "Guzmania Bromeliad",
      scientific: "Guzmania lingulata",
      category: "Outdoor Plants",
      badge: "Tropical Exotic",
      image: "https://images.unsplash.com/photo-1510251197878-a2e6d2cb590c?q=80&w=600&auto=format&fit=crop",
      light: "Filter morning light, partial shade",
      water: "Pour water directly into leaf cup center weekly",
      difficulty: "Medium",
      description: "A gorgeous epiphytic standout that gathers moisture from air. Its brilliant starburst centerpiece blooms stay vibrant for months.",
      toxicity: "Non-toxic to pets"
    }
  ];

  // Search & Filter Memo logic
  const filteredPlants = useMemo(() => {
    return plantList.filter((plant) => {
      const matchSearch = 
        plant.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plant.scientific.toLowerCase().includes(searchQuery.toLowerCase()) ||
        plant.description.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchCategory = 
        activeCategory === "All" || 
        plant.category === activeCategory;

      return matchSearch && matchCategory;
    });
  }, [searchQuery, activeCategory]);

  return (
    <section id="database" className="py-28 bg-gray-50/30 border-t border-gray-150/60 relative overflow-hidden">
      
      {/* Decorative Blur and Gradients */}
      <div className="absolute right-0 top-10 w-[550px] h-[550px] bg-green-500/[0.02] rounded-full blur-[120px] pointer-events-none -z-10" />
      <div className="absolute left-[-50px] bottom-10 w-[450px] h-[450px] bg-emerald-500/[0.025] rounded-full blur-[100px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Badge Area */}
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-1.5 px-3.5 py-1 bg-white border border-green-150/40 rounded-full text-green-750 text-xs font-extrabold uppercase tracking-widest shadow-xs backdrop-blur-md"
          >
            <span>📚</span> Plant Database
          </motion.div>

          {/* Heading */}
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-4xl sm:text-5xl font-[900] text-gray-950 tracking-tight"
          >
            Explore Thousands <br />
            Of Plant Species
          </motion.h2>

          {/* Subheading */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="text-lg text-gray-500 font-medium max-w-[650px] mx-auto leading-relaxed"
          >
            Discover detailed botanical summaries, care guidelines, watering frequency indicators and useful species insights.
          </motion.p>
        </div>

        {/* Large Modern Search Box Section */}
        <div className="max-w-xl mx-auto mb-12 relative">
          <div className="relative group">
            <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-green-650 transition-colors">
              <Search className="w-5 h-5" />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search any plant specimen (e.g. Monstera)..."
              className="w-full pl-13 pr-4 py-4 rounded-2xl bg-white border border-gray-200 focus:border-green-400 focus:ring focus:ring-green-100 focus:outline-none transition-all duration-200 text-gray-800 placeholder-gray-450 font-medium text-base shadow-xs group-hover:shadow-md"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-gray-650 transition-all cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Category Filters Carousel / Row */}
        <div className="flex flex-wrap justify-center items-center gap-2.5 mb-14">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-5 py-2.5 rounded-full text-xs font-bold transition-all duration-250 cursor-pointer ${
                activeCategory === cat 
                  ? "bg-green-600 text-white border border-green-650 shadow-md shadow-green-100/70 translate-y-[-1px]" 
                  : "bg-white text-gray-600 border border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Plant Cards Grid - Responsive columns: 4 on Desktop, 2 on Tablet, 1 on Mobile */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <AnimatePresence mode="popLayout">
            {filteredPlants.map((plant, idx) => (
              <motion.div
                key={plant.id}
                layout
                initial={{ opacity: 0, scale: 0.95, y: 15 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 15 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6 }}
                onClick={() => {
                  if (onPlantClick) {
                    const slug = plant.name.toLowerCase().replace(/\s+/g, '-');
                    onPlantClick(slug);
                  }
                }}
                className="group bg-white/90 border border-gray-150 rounded-[30px] overflow-hidden transition-all duration-300 flex flex-col justify-between cursor-pointer shadow-xs hover:shadow-md"
              >
                
                {/* Media Container with Botanical Illustration */}
                <div className="relative aspect-[4/3] overflow-hidden bg-gray-50/75 flex items-center justify-center p-6 border-b border-gray-100 group-hover:bg-green-50/20 transition-all">
                  <BotanicalIcon category={`${plant.category} ${plant.badge} ${plant.name}`} className="w-24 h-24" />
                  {/* Category overlay label */}
                  <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-md px-3 py-1 rounded-full text-[10px] font-black text-green-700 tracking-wider shadow-2xs border border-white/50">
                    {plant.badge}
                  </span>
                </div>

                {/* Content Panel Area */}
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-black text-gray-950 tracking-tight leading-tight group-hover:text-green-750 transition-colors">
                      {plant.name}
                    </h3>
                    <p className="text-xs text-gray-450 italic font-semibold mt-0.5">
                      {plant.scientific}
                    </p>
                    <p className="text-xs text-gray-550 font-medium leading-relaxed mt-3.5 line-clamp-2">
                      {plant.description}
                    </p>
                  </div>

                  {/* Actions & Button row */}
                  <div className="mt-6 pt-4 border-t border-gray-100 flex items-center justify-between gap-2.5">
                    <span className="text-[10px] text-gray-400 font-extrabold uppercase tracking-wide">
                      {plant.difficulty} care
                    </span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlant(plant);
                      }}
                      className="px-4 py-2 bg-gray-50 group-hover:bg-green-600 border border-gray-150 group-hover:border-green-650 text-gray-700 group-hover:text-white rounded-xl text-xs font-extrabold transition-all duration-200 flex items-center justify-center gap-1.5 cursor-pointer"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Quick View
                    </button>
                  </div>
                </div>

              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Empty state view */}
        {filteredPlants.length === 0 && (
          <div className="text-center py-20 bg-white border border-gray-100 rounded-3xl p-6">
            <span className="text-3xl">🧩</span>
            <h4 className="mt-3 text-lg font-black text-gray-900">No plant matching search</h4>
            <p className="text-sm text-gray-400 mt-1 max-w-sm mx-auto">
              We couldn't find any specimen for "{searchQuery}". Try selecting other filters or check for spelling errors.
            </p>
            <button 
              onClick={() => { setSearchQuery(""); setActiveCategory("All"); }}
              className="mt-6 px-5 py-2 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl text-xs font-black text-green-750 cursor-pointer"
            >
              Reset Search Filter
            </button>
          </div>
        )}

      </div>

      {/* QUICK VIEW POPUP MODAL (Aesthetic Glassmorphism popup container) */}
      <AnimatePresence>
        {selectedPlant && (
          <div className="fixed inset-0 bg-gray-950/40 backdrop-blur-md flex items-center justify-center z-99 p-4">
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.9, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 15 }}
              className="relative w-full max-w-2xl bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-150 p-0"
            >
              {/* Close Button on Top Right */}
              <button 
                onClick={() => setSelectedPlant(null)}
                className="absolute top-4 right-4 z-20 w-9 h-9 bg-black/55 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-all cursor-pointer border border-white/20"
                aria-label="Close details"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="grid grid-cols-1 md:grid-cols-5">
                
                {/* Image panel left side columns */}
                <div className="md:col-span-2 relative min-h-[220px] md:h-full bg-emerald-50/30 flex items-center justify-center p-6 border-b md:border-b-0 md:border-r border-gray-100">
                  <BotanicalIcon category={`${selectedPlant.category} ${selectedPlant.badge} ${selectedPlant.name}`} variant="illustration" className="w-full h-full max-w-[140px]" />
                </div>

                {/* Content info details columns */}
                <div className="md:col-span-3 p-8 space-y-6">
                  
                  <div className="space-y-1.5">
                    <span className="text-[10px] font-black text-green-550 uppercase tracking-widest block bg-green-50 px-2 py-0.5 rounded border border-green-100/50 max-w-fit">
                      {selectedPlant.badge}
                    </span>
                    <h3 className="text-2xl font-[900] text-gray-950 tracking-tight leading-none">
                      {selectedPlant.name}
                    </h3>
                    <p className="text-sm text-gray-450 italic mt-0.5">
                      {selectedPlant.scientific}
                    </p>
                  </div>

                  <p className="text-sm text-gray-550 leading-relaxed font-medium">
                    {selectedPlant.description}
                  </p>

                  {/* Botanical care grids indicators */}
                  <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    
                    {/* Light needs indicator icon */}
                    <div className="bg-amber-50/50 border border-amber-100/80 rounded-xl p-3 flex items-start gap-2">
                      <Sun className="w-5 h-5 text-amber-550 shrink-0 mt-0.5" />
                      <div className="space-y-0.5">
                        <span className="text-[10px] text-amber-600 font-extrabold uppercase tracking-wider block">Sunlight Requirements</span>
                        <span className="text-xs font-bold text-gray-800 leading-snug block">{selectedPlant.light}</span>
                      </div>
                    </div>

                    {/* Water needs indicator icon */}
                    <div className="bg-blue-50/50 border border-blue-100/80 rounded-xl p-3 flex items-start gap-2">
                      <Droplet className="w-5 h-5 text-blue-550 shrink-0 mt-0.5" />
                      <div className="space-y-0.5">
                        <span className="text-[10px] text-blue-600 font-extrabold uppercase tracking-wider block">Water Frequency</span>
                        <span className="text-xs font-bold text-gray-800 leading-snug block">{selectedPlant.water}</span>
                      </div>
                    </div>

                  </div>

                  {/* Pet Toxicity awareness alert box */}
                  <div className="bg-red-50/60 border border-red-105/40 rounded-xl p-3 flex items-center gap-2.5">
                    <ShieldAlert className="w-5 h-5 text-red-500 shrink-0" />
                    <div className="text-xs font-bold text-red-900 leading-tight">
                      <span className="text-red-700 font-black block text-[10px] uppercase tracking-wider mb-0.5">Toxicity Warning</span>
                      {selectedPlant.toxicity}
                    </div>
                  </div>

                  {/* Bottom dismiss trigger row */}
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <button 
                      onClick={() => setSelectedPlant(null)}
                      className="px-6 py-2.5 bg-gray-105 hover:bg-gray-150 text-gray-700 hover:text-gray-900 text-xs font-black rounded-xl transition-all cursor-pointer"
                    >
                      Close Window
                    </button>
                    {onPlantClick && (
                      <button 
                        onClick={() => {
                          const slug = selectedPlant.name.toLowerCase().replace(/\s+/g, '-');
                          onPlantClick(slug);
                          setSelectedPlant(null);
                        }}
                        className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white text-xs font-black rounded-xl transition-all cursor-pointer shadow-xs shadow-green-100/10"
                      >
                        Explore Page →
                      </button>
                    )}
                  </div>

                </div>

              </div>
            </motion.div>

          </div>
        )}
      </AnimatePresence>

    </section>
  );
}
